import React, { Fragment, useEffect, useMemo, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { Link, useParams } from "react-router-dom";
import moment from "moment-timezone";
import { formatTripDate } from "../../helpers/dates";
import { useMediaQuery } from "react-responsive";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";
import formatStage from "../../helpers/format-stage";
import formatPaymentType from "../../helpers/format-payment-type";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }
    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    default:
      break;
  }
  return state;
};

const SingleProspectTripHistoryDetailsLegData = ({ itineraryData }) => {
  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Leg Data</h6>

      <div className="prospect-table prospect-trip-history-leg-data">
        <table>
          <tbody>
          {!itineraryData && <tr>
            <td colSpan={3}>No itinerary exists.</td>
          </tr>}
          {itineraryData?.legs?.map((item, index) => (
            <tr key={item._id}>
              <td>Leg {index + 1}: {formatTripDate(item.departureDate)} {item.departureAirport?.code}-{item.arrivalAirport?.code}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetailsQuotes = ({ quotesData }) => {
  const data = useMemo(
    () =>
      quotesData.filter(({ liveQuoteLog, appOffer }) => liveQuoteLog.length > 0 || !!appOffer?.syncedAt).flatMap(quote =>
        quote.aircraftOptions.map(aircraftOption => {
          const { aircraftOptions, ...baseQuote } = quote;
          return {
            ...baseQuote,
            aircraftOption,
          };
        }),
      ).sort((a, b) => {
        const costDiff = a.aircraftOption?.totalCost - b.aircraftOption?.totalCost;
        if (costDiff !== 0) {
          return costDiff;
        }

        const nameA = a.aircraftOption?.aircraft?.name?.toLowerCase() || "";
        const nameB = b.aircraftOption?.aircraft?.name?.toLowerCase() || "";

        return nameA?.localeCompare(nameB);
      }),
    [quotesData],
  );

  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Quotes Sent / Synced</h6>

      <div className="prospect-table prospect-trip-history-quotes">
        <table>
          <thead>
          <tr>
            <th>Aircraft</th>
            <th>Total Cost</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!data.length && <tr>
            <td colSpan={3}>No quotes are sent or synced yet</td>
          </tr>}
          {data.map((item) => (
            <tr key={`${item._id}-${item.aircraftOption?.aircraft?.id}`}>
              <td>{item.aircraftOption?.aircraft?.name || "-"}</td>
              <td>{currencyFormatter.format(item.aircraftOption?.totalCost || 0)}</td>
              <td>
                <Link to={`/quotes/${item._id}`} target="_blank" rel="noopener noreferrer">
                  <span className="material-icons">visibility</span>
                </Link>
                <Link to={`/quotes/${item._id}/duplicate/${item.aircraftOption?._id}`} target="_blank" rel="noopener noreferrer" className="ml-1">
                  <span className="material-icons">content_copy</span>
                </Link>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetailsContracts = ({ contractsData }) => {
  const data = useMemo(
    () =>
      contractsData.sort((a, b) => {
        const costDiff = a.pricing?.flightCharges - b.pricing?.flightCharges;
        if (costDiff !== 0) {
          return costDiff;
        }

        const nameA = a.legs?.[0]?.aircraft?.name?.toLowerCase() || "";
        const nameB = b.legs?.[0]?.aircraft?.name?.toLowerCase() || "";

        return nameA?.localeCompare(nameB);
      }).map(item => ({
        ...item,
        aircrafts: [...new Set(item.legs.map(({ aircraft }) => aircraft?.name).filter(Boolean))],
      })),
    [contractsData],
  );

  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Contracts</h6>

      <div className="prospect-table prospect-trip-history-contracts">
        <table>
          <thead>
          <tr>
            <th>Aircraft</th>
            <th>Total Cost</th>
            <th>Stage</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!data.length && <tr>
            <td colSpan={4}>No contracts are saved yet</td>
          </tr>}
          {data.map((item) => (
            <tr key={item._id}>
              <td>{item.legs?.[0]?.aircraft?.name || "-"} {item.aircrafts.length > 1 ? `+ ${item.aircrafts.length - 1} more` : ""}</td>
              <td>{currencyFormatter.format(item.pricing?.flightCharges || 0)}</td>
              <td>{formatStage(item.stage)}</td>
              <td>
                <Link to={`/trips/${item.tripId}/contracts/${item._id}/preview`} target="_blank" rel="noopener noreferrer">
                  <span className="material-icons">visibility</span>
                </Link>
                <Link to={`/trips/${item.tripId}/contracts/${item._id}/duplicate`} target="_blank" rel="noopener noreferrer" className="ml-1">
                  <span className="material-icons">content_copy</span>
                </Link>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetailsOFRs = ({ ofrData }) => {
  return (
    <div className="prospect-trip-history-nested-table">
      <h6>OFR Data</h6>

      <div className="prospect-table prospect-trip-history-ofr-data">
        <table>
          <thead>
          <tr>
            <th>Operator</th>
            <th>Aircraft</th>
            <th>Operator Price</th>
            <th>Stage</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!ofrData.length && <tr>
            <td colSpan={4}>No operator financial records are saved yet</td>
          </tr>}
          {ofrData.sort((a, b) => a.operatorPrice - b.operatorPrice).map((item) => (
            <tr key={item._id}>
              <td>{item.operator?.name || "-"}</td>
              <td>{item.aircraft?.name || "-"}</td>
              <td>{currencyFormatter.format(item.operatorPrice || 0)}</td>
              <td>{formatStage(item.stage)}</td>
              <td>
                <Link to={`/trips/${item.tripId}?ofrId=${item._id}`} target="_blank" rel="noopener noreferrer">
                  <span className="material-icons">visibility</span>
                </Link>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetailsPassthroughData = ({ passthroughData }) => {
  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Passthrough Data</h6>

      {!!passthroughData ? <div className="prospect-table prospect-trip-history-passthrough-data">
        <table>
          <thead>
          <tr>
            <th>Date Converted</th>
            <th>Operator</th>
            <th>Aircraft</th>
            <th>Expected Commission</th>
          </tr>
          </thead>
          <tbody>
          {passthroughData && <tr>
            <td>{moment(passthroughData.convertedAt).format("M/D/YY")}</td>
            <td>{passthroughData.operator?.value || "-"}</td>
            <td>{passthroughData.aircraft?.name || "-"}</td>
            <td>{currencyFormatter.format(passthroughData.expectedCommission || 0)}</td>
          </tr>}
          </tbody>
        </table>
      </div> : <div className="prospect-table prospect-trip-history-passthrough-data-empty">
        <table>
          <tbody>
          <tr>
            <td colSpan={4}>Not converted to passthrough</td>
          </tr>
          </tbody>
        </table>
      </div>}
    </div>
  );
};

const SingleProspectTripHistoryDetailsClientPayments = ({ clientPaymentHistory }) => {
  const data = useMemo(
    () => clientPaymentHistory.filter(({ deleted }) => !deleted).sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)),
    [clientPaymentHistory],
  );

  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Client Payments</h6>

      <div className="prospect-table prospect-trip-history-client-payments">
        <table>
          <thead>
          <tr>
            <th>Payment Type</th>
            <th>Amount</th>
            <th>Date</th>
          </tr>
          </thead>
          <tbody>
          {!data.length && <tr>
            <td colSpan={3}>No client payments are saved yet</td>
          </tr>}
          {data.map((item) => (
            <tr key={item._id}>
              <td>{formatPaymentType(item.paymentType) || "-"}</td>
              <td>{currencyFormatter.format(item.amount || 0)}</td>
              <td>{moment(item.paymentDate).format("M/D/YY")}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetailsOperatorPayments = ({ operatorPaymentHistory }) => {
  const data = useMemo(
    () => operatorPaymentHistory.filter(({ deleted }) => !deleted).sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)),
    [operatorPaymentHistory],
  );

  return (
    <div className="prospect-trip-history-nested-table">
      <h6>Operator Payments</h6>

      <div className="prospect-table prospect-trip-history-operator-payments">
        <table>
          <thead>
          <tr>
            <th>Operator</th>
            <th>Amount</th>
            <th>Date</th>
          </tr>
          </thead>
          <tbody>
          {!data.length && <tr>
            <td colSpan={3}>No operator payments are saved yet</td>
          </tr>}
          {data.map((item) => (
            <tr key={item._id}>
              <td>{item.operator?.name || "-"}</td>
              <td>{currencyFormatter.format(item.amount || 0)}</td>
              <td>{moment(item.paymentDate).format("M/D/YY")}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SingleProspectTripHistoryDetails = ({ data }) => {
  return (
    <Grid container spacing={0}>
      <Grid item xs={12} sm={12} md={4} lg={3} order={1}>
        <SingleProspectTripHistoryDetailsLegData itineraryData={data.itineraryData}/>
      </Grid>
      <Grid item xs={12} sm={12} md={4} lg={3} order={{ xs: 2, sm: 2, md: 3, lg: 4 }}>
        <SingleProspectTripHistoryDetailsQuotes quotesData={data.quotesData}/>
      </Grid>
      <Grid item xs={12} sm={12} md={8} lg={6} order={{ xs: 3, sm: 3, md: 2, lg: 2 }}>
        <SingleProspectTripHistoryDetailsContracts contractsData={data.contractsData}/>
      </Grid>
      <Grid item xs={12} sm={12} md={8} lg={6} order={{ xs: 4, sm: 4, md: 4, lg: 5 }}>
        <SingleProspectTripHistoryDetailsOFRs ofrData={data.ofrData}/>
      </Grid>
      <Grid item xs={12} sm={12} md={4} lg={3} order={{ xs: 6, sm: 6, md: 5, lg: 3 }}>
        <SingleProspectTripHistoryDetailsClientPayments clientPaymentHistory={data.clientPaymentHistory}/>
      </Grid>
      <Grid item xs={12} sm={12} md={4} lg={3} order={{ xs: 7, sm: 7, md: 7, lg: 6 }}>
        <SingleProspectTripHistoryDetailsOperatorPayments operatorPaymentHistory={data.operatorPaymentHistory}/>
      </Grid>
      <Grid item xs={12} sm={12} md={8} lg={6} order={{ xs: 5, sm: 5, md: 6, lg: 8 }}>
        <SingleProspectTripHistoryDetailsPassthroughData passthroughData={data.passthroughData}/>
      </Grid>
      <Grid item lg={3} display={{ md: "none", lg: "block" }} order={7}/>
    </Grid>
  );
};

const SingleProspectTripHistory = () => {
  const { id } = useParams();

  const initialState = {
    loading: true,
    data: [],
    totalLength: 0,
    query: "",
    countPerPage: 10,
    currentPage: 1,
    sortBy: "createdAt",
    sortDirection: "desc",
  };
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);
  const isMobile = useMediaQuery({ query: "(max-width: 899px)" });

  const columns = useMemo(() => [
    {
      name: "Created at",
      selector: "createdAt",
      sortable: true,
      cell: (row) => {
        return row.createdAt ? moment(row.createdAt).format("M/D/YY - HHmm") : "-";
      },
      minWidth: "80px",
      maxWidth: "110px",
      omit: isMobile,
    },
    {
      name: "Trip #",
      selector: "tripTag",
      sortable: true,
      cell: (row) => {
        return (
          <Link className="ellipsis-cell" to={`/trips/${row._id}`} target="_blank" rel="noopener noreferrer">
            <Tooltip title={row.tripTag} arrow>
              {row.tripTag}
            </Tooltip>
          </Link>
        );
      },
      width: "80px",
      omit: isMobile,
    },
    {
      name: "Stage",
      selector: "displayStage",
      sortable: true,
      minWidth: "100px",
      cell: (row) => {
        switch (row.displayStage) {
          case "newTrip":
            return "Active";
          case "ended":
            return "Ended";
          case "canceled":
            return "Canceled";
          case "archived":
            return "Archived";
          case "closedPassthrough":
            return "Closed - Passthrough";
          default:
            return "Unknown";
        }
      },
      omit: isMobile,
    },
    {
      name: "Dates",
      selector: "firstDepartureDate",
      sortable: true,
      minWidth: "80px",
      cell: (row) => {
        if (!row.itineraryData || !row.itineraryData.legs.length) {
          return "N/A";
        }

        if (row.itineraryData.legs.length === 1) {
          return formatTripDate(row.firstDepartureDate);
        }

        return `${formatTripDate(row.firstDepartureDate)} - ${formatTripDate(row.lastDepartureDate)}`;
      },
      omit: isMobile,
    },
    {
      name: "Routing",
      selector: "departureAirport",
      sortable: true,
      minWidth: "80px",
      cell: (row) => {
        if (!row.itineraryData) {
          return "N/A";
        }

        return `${row.departureAirport}-${row.arrivalAirport}`;
      },
      omit: isMobile,
    },
    {
      name: "Client Payments",
      selector: "clientPayments",
      sortable: true,
      minWidth: "100px",
      cell: (row) => {
        return currencyFormatter.format(row.clientPayments || 0);
      },
      omit: isMobile,
    },
    {
      name: "Operator Payments",
      selector: "operatorPayments",
      sortable: true,
      minWidth: "100px",
      cell: (row) => {
        return currencyFormatter.format(row.operatorPayments || 0);
      },
      omit: isMobile,
    },
    {
      name: "Operator",
      selector: "operator",
      sortable: true,
      minWidth: "95px",
      cell: (row) => {
        if (!row.operators.length) {
          return "N/A";
        }

        const operators = [...new Set(row.operators)];

        let result = operators[0];
        if (operators.length > 1) {
          result = `${result} + ${operators.length - 1} more`;
        }

        return result;
      },
      omit: isMobile,
    },
    {
      name: "Aircraft",
      selector: "aircraft",
      sortable: true,
      minWidth: "95px",
      cell: (row) => {
        if (!row.aircrafts.length) {
          return "N/A";
        }

        const aircrafts = [...new Set(row.aircrafts)];

        let result = aircrafts[0];
        if (aircrafts.length > 1) {
          result = `${result} + ${aircrafts.length - 1} more`;
        }

        return result;
      },
      omit: isMobile,
    },
    {
      name: "Trip Info",
      selector: "mobile",
      sortable: false,
      cell: (row) => {
        return <div className="prospect-trip-history-mobile-data">
          <span className="material-icons prospect-trip-history-mobile-icon">flight</span>
          {row.itineraryData ? `${row.departureAirport} - ${row.arrivalAirport}` : "N/A"}
          <br/>
          <span className="material-icons prospect-trip-history-mobile-icon">date_range</span>
          {!row.itineraryData || !row.itineraryData.legs.length ? "N/A" : row.itineraryData.legs.length === 1 ? formatTripDate(row.firstDepartureDate) : `${formatTripDate(row.firstDepartureDate)} - ${formatTripDate(row.lastDepartureDate)}`}
          <br/>
          <span className="material-icons prospect-trip-history-mobile-icon">attach_money</span>
          {currencyFormatter.format(row.clientPayments || 0)}
        </div>;
      },
      omit: !isMobile,
    },
  ], [isMobile]);

  const getData = async (query, loadSilently) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(`/api/prospects/${id}/trip-history`, {
        signal,
        params: {
          query: query.trim(),
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  useEffect(() => {
    getData(state.query);
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Trip History"
        rightColumn={
          <div
            id="prospect-trip-history-actions-row"
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Search
              placeholder="Search"
              onSearch={handleSearch}
              query={state.query}
              className="prospect-trip-history-search"
            />
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container prospect-trip-history-table">
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 25]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          expandableRows
          expandableRowsComponent={<SingleProspectTripHistoryDetails/>}
        />
      </div>
    </Fragment>
  );
};

export default SingleProspectTripHistory;
